import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Box,
  Typography,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  Grid,
  Paper,
  Tabs,
  Tab,
} from "@mui/material";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import SearchIcon from "@mui/icons-material/Search";
import jsQR from "jsqr";
import { LABEL_FIELDS } from "../labelSchema";
import { lookupScannedLabel } from "../../../services/LabelTemplate";

const fieldLabel = (key) => {
  const known = LABEL_FIELDS.find((f) => f.key === key);
  return known ? known.label : key;
};

const when = (v) => (v ? String(v).replace("T", " ").slice(0, 19) : "—");

/**
 * Scan a printed sticker and show what the system recorded for it.
 *
 * Two inputs for the same lookup: the device camera, and a text box for a USB
 * barcode scanner (which types the payload like a keyboard) or a pasted code.
 */
export default function ScanLabelDialog({ open, onClose }) {
  const [tab, setTab] = useState(0);
  const [payload, setPayload] = useState("");
  const [looking, setLooking] = useState(false);
  const [result, setResult] = useState(null);
  const [cameraError, setCameraError] = useState(null);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);

  const lookup = useCallback(async (text) => {
    if (!text || !text.trim()) return;
    setLooking(true);
    setResult(null);
    const data = await lookupScannedLabel(text.trim());
    setLooking(false);
    setResult(
      data || {
        found: false,
        message: "Lookup failed. Check that you are still signed in.",
      }
    );
  }, []);

  const stopCamera = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const startCamera = useCallback(async () => {
    setCameraError(null);
    // getUserMedia needs a secure context: https, or http on localhost. Served from
    // a plain LAN address the browser blocks it outright, so say so rather than
    // leaving a black rectangle.
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setCameraError(
        window.isSecureContext
          ? "This browser does not expose a camera."
          : "The camera needs HTTPS (or localhost). Use the Enter code tab instead."
      );
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }

      const tick = () => {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        if (video && canvas && video.readyState === video.HAVE_ENOUGH_DATA) {
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const code = jsQR(img.data, img.width, img.height, {
            inversionAttempts: "dontInvert",
          });
          if (code && code.data) {
            setPayload(code.data);
            stopCamera();
            lookup(code.data);
            return;
          }
        }
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    } catch (e) {
      setCameraError(
        e && e.name === "NotAllowedError"
          ? "Camera permission was refused. Allow it, or use the Enter code tab."
          : "Could not open the camera. Use the Enter code tab instead."
      );
    }
  }, [lookup, stopCamera]);

  // Camera runs only while its tab is visible and the dialog is open.
  useEffect(() => {
    if (open && tab === 0) startCamera();
    else stopCamera();
    return stopCamera;
  }, [open, tab, startCamera, stopCamera]);

  useEffect(() => {
    if (!open) {
      setPayload("");
      setResult(null);
      setCameraError(null);
      setTab(0);
    }
  }, [open]);

  const run = result && result.run;
  const runFields = (run && run.fields) || {};

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <QrCodeScannerIcon color="info" />
          <Typography variant="h6">Scan a label</Typography>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
          <Tab label="Camera" />
          <Tab label="Enter code" />
        </Tabs>

        {tab === 0 && (
          <Box sx={{ mb: 2 }}>
            {cameraError ? (
              <Alert severity="info" sx={{ fontSize: 12 }}>
                {cameraError}
              </Alert>
            ) : (
              <Box
                sx={{
                  position: "relative",
                  bgcolor: "common.black",
                  borderRadius: 1,
                  overflow: "hidden",
                }}
              >
                <video
                  ref={videoRef}
                  muted
                  playsInline
                  style={{ width: "100%", display: "block", maxHeight: 320, objectFit: "cover" }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    m: "auto",
                    width: "60%",
                    aspectRatio: "1 / 1",
                    border: "2px solid rgba(255,255,255,0.8)",
                    borderRadius: 1,
                    pointerEvents: "none",
                  }}
                />
              </Box>
            )}
            <canvas ref={canvasRef} style={{ display: "none" }} />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
              Hold the sticker inside the frame. It reads automatically.
            </Typography>
          </Box>
        )}

        {tab === 1 && (
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <TextField
              autoFocus
              fullWidth
              size="small"
              label="Scanned code"
              placeholder="NH-100|100005"
              value={payload}
              onChange={(e) => setPayload(e.target.value)}
              onKeyDown={(e) => {
                // A USB barcode scanner types the payload then presses Enter.
                if (e.key === "Enter") lookup(payload);
              }}
              helperText="Paste it, or use a USB scanner — it types and presses Enter"
            />
            <Button
              variant="contained"
              startIcon={looking ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
              disabled={looking || !payload.trim()}
              onClick={() => lookup(payload)}
              sx={{ flexShrink: 0, mt: 0.25 }}
            >
              Look up
            </Button>
          </Stack>
        )}

        {looking && tab === 0 && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {result && !result.found && (
          <Alert severity="warning" sx={{ fontSize: 12 }}>
            {result.message || "No printed label matches that code."}
            {payload ? (
              <>
                <br />
                Scanned: <code>{payload}</code>
              </>
            ) : null}
          </Alert>
        )}

        {result && result.found && run && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Chip size="small" color="success" label="Found" />
              <Typography fontWeight={700} sx={{ flex: 1, minWidth: 0 }} noWrap>
                {run.productName || "Unknown product"}
              </Typography>
              <Chip size="small" variant="outlined" label={result.barcode} />
            </Stack>
            <Divider sx={{ mb: 1.5 }} />

            <Grid container spacing={1.5}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Serial
                </Typography>
                <Typography fontWeight={700}>{result.serial}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Label
                </Typography>
                <Typography>{result.labelName || "—"}</Typography>
              </Grid>

              {Object.keys(runFields).map((key) => (
                <Grid item xs={6} key={key}>
                  <Typography variant="caption" color="text.secondary">
                    {fieldLabel(key)}
                  </Typography>
                  <Typography>{String(runFields[key] || "—")}</Typography>
                </Grid>
              ))}

              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Printed by
                </Typography>
                <Typography>{run.printedBy || "—"}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Printed at
                </Typography>
                <Typography>{when(run.printedAt)}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  From run
                </Typography>
                <Typography variant="body2">
                  serials {run.serialStart} – {run.serialStart + run.serialCount - 1} (
                  {run.serialCount} label{run.serialCount > 1 ? "s" : ""})
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        )}
      </DialogContent>

      <DialogActions>
        {result && (
          <Button
            onClick={() => {
              setResult(null);
              setPayload("");
              if (tab === 0) startCamera();
            }}
          >
            Scan another
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
